import { Circle, Download, Square, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { Recording } from '@bccomms/shared';
import { session } from '../../lib/session';
import { useStore } from '../../store';
import { Field, fmtTime } from '../ui';

function fmtDuration(ms: number): string {
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function RecordingsTab() {
  const channels = useStore((s) => s.channels);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [channelId, setChannelId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setRecordings((await session.admin('admin.record.list', {})) as Recording[]);
    } catch (err) {
      console.warn('record.list failed', err);
    }
  }, []);
  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 3000);
    return () => clearInterval(t);
  }, [refresh]);
  useEffect(() => {
    if (!channelId && channels[0]) setChannelId(channels[0].id);
  }, [channels, channelId]);

  const start = async () => {
    if (!channelId) return;
    setBusy(true);
    setError(null);
    try {
      await session.admin('admin.record.start', { channelId });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const stop = async (id: string) => {
    await session.admin('admin.record.stop', { recordingId: id }).catch((e: Error) => setError(e.message));
    await refresh();
  };
  const del = async (id: string) => {
    if (!confirm('Delete this recording and its files permanently?')) return;
    await session.admin('admin.record.delete', { recordingId: id }).catch((e: Error) => setError(e.message));
    await refresh();
  };
  const download = (recordingId: string, trackIndex: number) => {
    const token = localStorage.getItem('bccomms.token') ?? '';
    window.open(`/api/admin/recordings/${recordingId}/${trackIndex}?token=${encodeURIComponent(token)}`, '_blank');
  };

  const active = recordings.filter((r) => r.state === 'recording');
  const past = recordings.filter((r) => r.state === 'stopped');

  return (
    <>
      <div className="admin__title">
        <h2>Recordings</h2>
      </div>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-title">Start a recording</div>
        <p className="muted">
          Records an isolated track per speaker who talks on the channel — one file per person, not a single mix. Talk pauses leave gaps in a track rather than
          silence, so a track's length reflects talk time, not session time.
        </p>
        <div className="row">
          <Field label="Channel">
            <select className="select" style={{ width: 220 }} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.short})
                </option>
              ))}
            </select>
          </Field>
          <button className="btn btn--primary" style={{ marginTop: 6 }} disabled={busy || !channelId || active.some((r) => r.channelId === channelId)} onClick={() => void start()}>
            <Circle size={14} fill="currentColor" /> Start recording
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </div>

      {active.length > 0 && (
        <>
          <div className="section-title">Recording now</div>
          {active.map((r) => (
            <div key={r.id} className="card" style={{ marginBottom: 12 }}>
              <div className="row row--between">
                <div className="row">
                  <span className="dot dot--live" />
                  <strong>{r.channelName}</strong>
                  <span className="chip chip--mono">since {fmtTime(r.startedAt)}</span>
                  <span className="chip">{r.tracks.length} track(s)</span>
                </div>
                <button className="btn btn--danger btn--sm" onClick={() => void stop(r.id)}>
                  <Square size={14} /> Stop
                </button>
              </div>
              {r.tracks.length > 0 && (
                <div className="row row--wrap" style={{ marginTop: 8, gap: 6 }}>
                  {r.tracks.map((t) => (
                    <span key={t.userId} className="chip chip--success">
                      {t.username} {t.stoppedAt === null ? '· live' : '· ended'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      <div className="section-title">Past recordings</div>
      {past.length === 0 && <p className="muted">None yet.</p>}
      {past.map((r) => (
        <div key={r.id} className="card" style={{ marginBottom: 12 }}>
          <div className="row row--between">
            <div className="row">
              <strong>{r.channelName}</strong>
              <span className="chip chip--mono">
                {fmtTime(r.startedAt)} · {r.stoppedAt ? fmtDuration(r.stoppedAt - r.startedAt) : '—'}
              </span>
            </div>
            <button className="iconbtn" aria-label="Delete recording" title="Delete" onClick={() => void del(r.id)}>
              <Trash2 size={16} />
            </button>
          </div>
          {r.tracks.length === 0 ? (
            <p className="muted" style={{ marginTop: 8 }}>
              No one talked on this channel while it was recording — no tracks were captured.
            </p>
          ) : (
            <table className="table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Speaker</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {r.tracks.map((t, i) => (
                  <tr key={t.userId}>
                    <td>{t.username}</td>
                    <td className="mono">{t.stoppedAt ? fmtDuration(t.stoppedAt - t.startedAt) : '—'}</td>
                    <td className="mono">{(t.bytes / 1024).toFixed(0)} KB</td>
                    <td>
                      <button className="iconbtn" aria-label="Download" title="Download" onClick={() => download(r.id, i)}>
                        <Download size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </>
  );
}
